/**!
 *  Skill-building editor.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React, {useState} from 'react'
import styles from './skillBuilding.module.scss'

import API from 'Class/API'
import Globals from 'Class/Globals'

import IconButton from 'Components/UI/IconButton'
import TabCreate from './tab.create'
import TabEdit from './tab.edit'
import TabOverview from './tab.overview'
import TabMenu from "Components/UI/TabMenu"

export const ViewSkillBuilding = ({contextId}) =>
{
    const [message, setMessage] = useState(null)
    const [loading, setLoading] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const [selectedTab, setSelectedTab] = useState('overview')

    const onCancelEdit = () =>
    {
        setMessage(null)
        setSelectedItem(null)
        setSelectedTab('overview')
    }

    const onClose = () =>
    {
        Globals.CloseSkillBuildingEditor()
    }

    const onCreate = async (data) =>
    {
        setMessage(null)
        setLoading(true)
        data.community = contextId
        data.image = parseImage(data.image)
        data.topics = data.topics.map(topic => Array.isArray(topic) ? topic[0] : topic)
        const {id, error, message} = await API.RequestAsync('skillActivities/create', data)
        setLoading(false)
        if (!id || error)
        {
            setMessage('Failed to create technical skill: ' + (message || 'Unknown error'))
            return false
        }
        setMessage('Saved technical skill')
        setSelectedTab('overview')
        return true
    }

    const onDelete = (item) =>
    {
        return new Promise((resolve) =>
        {
            Globals.DialogCreate({
                confirmLabel: "Delete it",
                message: "Are you sure you want to permanently delete this technical skill?",
                onConfirm: async () =>
                {
                    setMessage(null)
                    setLoading(true)
                    const {error, message} = await API.RequestAsync('skillActivities/delete',
                    {
                        community: contextId,
                        id: item.id
                    })
                    setLoading(false)
                    if (error)
                    {
                        setMessage('Failed to delete technical skill: ' + (message || 'Unknown error'))
                        resolve(false)
                        return
                    }
                    setMessage('Deleted technical skill')
                    resolve(true)
                },
                title: "Confirm deletion",
                type: "confirm"
            })
        })
    }

    const onEdit = (item) =>
    {
        setMessage(null)
        setSelectedItem(item)
        setSelectedTab('edit')
    }

    const onSave = async (input) =>
    {
        setMessage(null)
        setLoading(true)
        const data = {
            id: input.id,
            name: input.name,
            community: contextId,
            description: input.description,
            image: parseImage(input.image),
            activities: input.activities,
            specializations: input.specializations,
            topic: input.topic ? (Array.isArray(input.topic) ? input.topic[0] : input.topic) : null
        }
        const {error, message} = await API.RequestAsync('skillActivities/edit', data)
        setLoading(false)
        if (error)
        {
            setMessage('Failed to save technical skill: ' + message)
            return false;
        }
        setMessage('Saved technical skill')
        setSelectedItem(input)
        return true;
    }

    const onTab = (_, tab) =>
    {
        setMessage(null)
        setSelectedTab(tab)
    }

    const parseImage = (input) =>
    {
        if (!Array.isArray(input))
        {
            return input
        }
        return input.length > 0 ? input[0] : null
    }

    let content;

    switch(selectedTab) {
        case 'create':
            content = <TabCreate
                community={contextId}
                loading={loading}
                message={message}
                onBack={() => setSelectedTab('overview')}
                onClose={onClose}
                onCreate={onCreate}
            />
            break
        case 'edit':
            content = <TabEdit
                community={contextId}
                item={selectedItem}
                loading={loading}
                message={message}
                onCancel={onCancelEdit}
                onClose={onClose}
                onSave={onSave}
            />
            break
        default:
            content = <TabOverview
                community={contextId}
                message={message}
                onClose={onClose}
                onCreate={() => setSelectedTab('create')}
                onDelete={onDelete}
                onEdit={onEdit}
            />
    }

    return (
        <div className={styles.editor}>
            <div className={styles.content}>
                <div className={styles.top}>
                    <TabMenu
                        disabled={{edit: !selectedItem}}
                        items={{
                            overview: 'Overview',
                            create: 'Create New',
                            edit: 'Edit'
                        }}
                        onClick={onTab}
                        selected={selectedTab}
                    />
                    <IconButton
                        className={styles.close}
                        feather="X"
                        onClick={onClose}
                    />
                </div>
                {content}
            </div>
        </div>
    )
};

export default ViewSkillBuilding